// TEMP: mock disconnect dialogs for the simulation panel — every state the "Disconnect sales channel" modal can take,
// rendered from fake data so each can be previewed in isolation. Mirrors the real markup in salesChannel.tsx
// (SalesChannelDisconnectWarning); the real one is data-driven (a Relay query computes the affected products), which is
// why these are self-contained copies rather than the real component. Delete with the rest of the simulation panel.
import React, { FC, ReactNode } from 'react';
import { BasicButton, Link } from 'src/components/widgets/buttons';
import { Dialog, DialogWindow } from 'src/components/widgets/dialog';
import { Icon } from 'src/components/widgets/icon';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { Spinner } from 'src/components/widgets/spinner';
import { Center } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import OpenInNewIcon from 'src/shared/material_icons/action/open_in_new.svg';
import WarningTriangleIcon from 'src/shared/material_icons/alert/warning_amber.svg';
import styled from 'styled-components';

export type DisconnectSimulationVariant =
    'secondaryConfirm' | 'secondaryMany' | 'masterBreak' | 'masterConfirm' | 'uninstallSteps' | 'uninstallConfirm' | 'uninstallMaster' | 'loading';

const HELP_URL = 'https://zakeke.zendesk.com/hc/en-us/articles/4415810409106-Sales-Channels#h_01G5S8F7PSYM7H0MHD44K1G6FM';

interface MockProduct {
    id: string;
    code: string;
    name: string;
    smallImageUrl?: string | null;
}

const MOCK_PRODUCTS: MockProduct[] = [
    { id: 'p1', code: 'TSHIRT-001', name: 'Classic Cotton T-Shirt' },
    { id: 'p2', code: 'MUG-014', name: 'Ceramic Mug 11oz' },
    { id: 'p3', code: 'HOODIE-233', name: 'Premium Pullover Hoodie' }
];

/* ---------- Styled (mirrors salesChannel.tsx) ---------- */

const DisconnectDialogWindow = styled(DialogWindow)`
    flex-basis: 780px;
    max-width: 780px;
`;

const DisconnectDialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 12px;
`;

const DisconnectHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-right: 24px;
`;

const DisconnectHeaderTitle = styled.h1`
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.3;
    color: ${(props) => props.theme.app.textColor};
`;

const DisconnectHeaderMeta = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 13px;
    color: ${(props) => props.theme.app.primary};
`;

const LinkPanel = styled.div`
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 8px;
`;

const LinkPanelHeader = styled.div`
    display: grid;
    gap: 2px;
    padding: 12px 14px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.pageBackgroundColor};
`;

const LinkPanelTitle = styled.strong`
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const LinkPanelFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 10px 14px;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
`;

const DisconnectInlineLink = styled.button`
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    color: ${(props) => props.theme.app.secondary};
    text-decoration: underline;
    cursor: pointer;
`;

const ProductList = styled.div`
    max-height: 260px;
    overflow-y: auto;
`;

const ProductItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-bottom: 1px solid ${(props) => props.theme.app.borderColor};

    &:last-child {
        border-bottom: none;
    }
`;

const ProductThumb = styled.div<{ $src?: string | null }>`
    width: 40px;
    height: 40px;
    flex-shrink: 0;
    border-radius: 6px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    background-image: ${(props) => (props.$src ? `url("${props.$src}")` : 'none')};
    background-size: cover;
    background-position: center;
`;

const ProductInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 2px;
`;

const ProductName = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 500;
    color: ${(props) => props.theme.app.textColor};
`;

const ProductMeta = styled.span`
    font-size: 12px;
    color: ${(props) => props.theme.app.primary};
`;

const LinkProductButton = styled(BasicButton)`
    flex-shrink: 0;
    white-space: nowrap;
`;

const StepsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const StepDivider = styled.div`
    height: 1px;
    background: ${(props) => props.theme.app.borderColor};
`;

const Step = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 14px;
`;

const StepNumber = styled.div`
    flex: 0 0 auto;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: ${(props) => props.theme.app.primary_dark};
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 600;
`;

const StepBody = styled.div`
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 8px;
`;

const StepSplit = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
`;

const StepText = styled.div`
    flex: 1;
    min-width: 0;
    display: grid;
    gap: 8px;
`;

const StepTitle = styled.strong`
    font-size: 15px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
`;

const StepTitleMuted = styled.span`
    font-weight: 400;
    color: ${(props) => props.theme.app.primary};
`;

const StepDescription = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    color: ${(props) => props.theme.app.primary};
`;

const OpenAdminButton = styled(BasicButton)`
    flex-shrink: 0;
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    gap: 6px;

    svg {
        width: 16px;
        height: 16px;
    }
`;

const DisconnectConfirmationMessage = styled.p`
    margin: 0;
    color: ${(props) => props.theme.app.textColor};
    font-size: 14px;
    line-height: 1.5;
`;

const DisconnectDialogLoadingState = styled(Center)`
    flex-direction: column;
    gap: 12px;
    min-height: 120px;
    color: ${(props) => props.theme.app.textColor};
    font-size: 13px;
`;

/* ---------- Pieces ---------- */

const DisconnectDangerBanner: FC<{ title: ReactNode; children: ReactNode }> = ({ title, children }) => (
    <NotificationBanner type='WARNING' icon={<WarningTriangleIcon />} title={title} content={children} />
);

const DisconnectHeaderView: FC<{ storeName: string; storeDomain?: string; title?: ReactNode }> = ({ storeName, storeDomain, title }) => (
    <DisconnectHeader>
        <DisconnectHeaderTitle>
            {title ??
                (storeName ? (
                    <>
                        {T._('Before you disconnect', 'Admin')} {storeName}
                    </>
                ) : (
                    T._('Disconnect sales channel', 'Admin')
                ))}
        </DisconnectHeaderTitle>
        <DisconnectHeaderMeta>
            {storeName && <span>{storeName}</span>}
            {storeName && storeDomain && <span>·</span>}
            {storeDomain && <span>{storeDomain}</span>}
        </DisconnectHeaderMeta>
    </DisconnectHeader>
);

const ProductRow: FC<{ product: MockProduct; withAction?: boolean }> = ({ product, withAction }) => (
    <ProductItem>
        <ProductThumb $src={product.smallImageUrl} />
        <ProductInfo>
            <ProductName>{product.name}</ProductName>
            <ProductMeta>
                {T._('ID', 'Admin')} {product.code}
            </ProductMeta>
        </ProductInfo>
        {withAction && (
            <LinkProductButton type='button' $size='small' onClick={() => undefined}>
                {T._('Add to online store', 'Admin')}
            </LinkProductButton>
        )}
    </ProductItem>
);

const DisconnectBody: FC<{
    products: MockProduct[];
    isMaster: boolean;
    uninstall: boolean;
    storeName: string;
    onViewProducts: () => void;
}> = ({ products, isMaster, uninstall, storeName, onViewProducts }) => {
    if (products.length === 0) {
        return (
            <DisconnectConfirmationMessage>
                {uninstall ? (
                    T._('To disconnect, remove the Zakeke app from your store.', 'Admin')
                ) : (
                    <>
                        {T._('Do you want to disconnect', 'Admin')}{' '}
                        <strong>{storeName || (isMaster ? T._('this Master Integration', 'Admin') : T._('this Sales Channel', 'Admin'))}</strong>?
                    </>
                )}
            </DisconnectConfirmationMessage>
        );
    }

    const affectedCount = products.length;
    const bannerTitle = isMaster
        ? T._('Important: products will break', 'Admin')
        : affectedCount === 1
          ? T._('1 product will be permanently deleted', 'Admin')
          : `${affectedCount} ${T._('products will be permanently deleted', 'Admin')}`;

    return (
        <>
            <DisconnectDangerBanner title={bannerTitle}>
                {isMaster ? (
                    T._(
                        'Products originating from or published on the Master Integration will no longer be available for customization on your store. They will remain in the product list and be marked as broken.',
                        'Admin'
                    )
                ) : (
                    <>
                        {T._('They were created from products of', 'Admin')} <strong>{storeName || T._('this store', 'Admin')}</strong>{' '}
                        {T._("and aren't linked to a product in any other sales channel.", 'Admin')}{' '}
                        {uninstall
                            ? T._("They'll be deleted when you uninstall the app, and this can't be undone.", 'Admin')
                            : T._("They'll be deleted when you disconnect, and this can't be undone.", 'Admin')}
                    </>
                )}
            </DisconnectDangerBanner>

            {isMaster ? (
                <LinkPanel>
                    <LinkPanelHeader>
                        <LinkPanelTitle>{T._('Products that will be marked as broken', 'Admin')}</LinkPanelTitle>
                    </LinkPanelHeader>
                    <ProductList>
                        {products.map((product) => (
                            <ProductRow key={product.id} product={product} />
                        ))}
                    </ProductList>
                </LinkPanel>
            ) : (
                <StepsContainer>
                    <Step>
                        <StepNumber>1</StepNumber>
                        <StepBody>
                            <StepTitle>
                                {T._('Keep the products you need', 'Admin')} <StepTitleMuted>({T._('optional', 'Admin')})</StepTitleMuted>
                            </StepTitle>
                            <StepDescription>
                                {T._(
                                    "Link a product to a matching one in another store and it won't be deleted. You can do it here or later from Customizable Products.",
                                    'Admin'
                                )}{' '}
                                <Link href={HELP_URL} target='_blank'>
                                    {T._('Learn how linking works', 'Admin')}
                                </Link>
                            </StepDescription>
                            <LinkPanel>
                                <ProductList>
                                    {products.map((product) => (
                                        <ProductRow key={product.id} product={product} withAction />
                                    ))}
                                </ProductList>
                                <LinkPanelFooter>
                                    <DisconnectInlineLink type='button' onClick={onViewProducts}>
                                        {affectedCount === 1
                                            ? T._('View 1 product in Customizable Products', 'Admin')
                                            : `${T._('View', 'Admin')} ${affectedCount} ${T._('products in Customizable Products', 'Admin')}`}
                                    </DisconnectInlineLink>
                                </LinkPanelFooter>
                            </LinkPanel>
                        </StepBody>
                    </Step>

                    {uninstall && (
                        <>
                            <StepDivider />
                            <Step>
                                <StepNumber>2</StepNumber>
                                <StepBody>
                                    <StepSplit>
                                        <StepText>
                                            <StepTitle>{T._('Disconnect the store', 'Admin')}</StepTitle>
                                            <StepDescription>
                                                {T._(
                                                    'This sales channel can only be disconnected by uninstalling the Zakeke app from the Shopify admin of',
                                                    'Admin'
                                                )}{' '}
                                                {storeName || T._('your store', 'Admin')}.{' '}
                                                {T._("Do this after step 1 — any product that isn't linked by then will be permanently deleted.", 'Admin')}
                                            </StepDescription>
                                        </StepText>
                                        <OpenAdminButton
                                            type='button'
                                            $size='small'
                                            onClick={() => window.open('https://admin.shopify.com', '_blank', 'noopener,noreferrer')}
                                        >
                                            {T._('Open Shopify admin', 'Admin')}
                                            <Icon>
                                                <OpenInNewIcon />
                                            </Icon>
                                        </OpenAdminButton>
                                    </StepSplit>
                                </StepBody>
                            </Step>
                        </>
                    )}
                </StepsContainer>
            )}
        </>
    );
};

const VARIANT_CONFIG: Record<
    Exclude<DisconnectSimulationVariant, 'loading' | 'uninstallMaster'>,
    { products: MockProduct[]; isMaster: boolean; uninstall: boolean }
> = {
    secondaryConfirm: { products: [], isMaster: false, uninstall: false },
    secondaryMany: { products: MOCK_PRODUCTS, isMaster: false, uninstall: false },
    masterBreak: { products: MOCK_PRODUCTS, isMaster: true, uninstall: false },
    masterConfirm: { products: [], isMaster: true, uninstall: false },
    uninstallSteps: { products: MOCK_PRODUCTS, isMaster: false, uninstall: true },
    uninstallConfirm: { products: [], isMaster: false, uninstall: true }
};

/** A single disconnect modal in the state selected by `variant`, driven entirely by mock data. */
export const DisconnectSimulationDialog: FC<{ variant: DisconnectSimulationVariant; storeName: string; storeDomain?: string; onClose: () => void }> = ({
    variant,
    storeName,
    storeDomain,
    onClose
}) => {
    // Still checking which products are affected.
    if (variant === 'loading') {
        return (
            <Dialog windowDecorator={DisconnectDialogWindow} minHeight={460} onClose={onClose}>
                <DisconnectDialogContent>
                    <DisconnectHeaderView storeName={storeName} storeDomain={storeDomain} />
                    <DisconnectDialogLoadingState role='status' aria-live='polite'>
                        <Spinner />
                        <span>{T._('Checking affected products...', 'Admin')}</span>
                    </DisconnectDialogLoadingState>
                </DisconnectDialogContent>
            </Dialog>
        );
    }

    // Master on Shopify: the affected products are not loaded, only the uninstall-the-app banner is shown.
    if (variant === 'uninstallMaster') {
        return (
            <Dialog windowDecorator={DisconnectDialogWindow} minHeight={460} onClose={onClose}>
                <DisconnectDialogContent>
                    <DisconnectHeaderView storeName={storeName} storeDomain={storeDomain} />
                    <DisconnectDangerBanner title={T._('Important: products will break', 'Admin')}>
                        {T._('To disconnect this Sales Channel, uninstall the Zakeke app from', 'Admin')}{' '}
                        <strong>{storeName || T._('this store', 'Admin')}</strong>.{' '}
                        {T._(
                            'Products originating from or published on it will no longer be available for customization and will be marked as broken in the product list.',
                            'Admin'
                        )}
                    </DisconnectDangerBanner>
                </DisconnectDialogContent>
            </Dialog>
        );
    }

    const { products, isMaster, uninstall } = VARIANT_CONFIG[variant];
    // The Shopify-secondary "uninstall steps" modal leads with "Disconnect {store}" instead of "Before you disconnect {store}".
    const headerTitle = variant === 'uninstallSteps' ? `${T._('Disconnect', 'Admin')} ${storeName}` : undefined;
    const content = (
        <DisconnectDialogContent>
            <DisconnectHeaderView storeName={storeName} storeDomain={storeDomain} title={headerTitle} />
            <DisconnectBody products={products} isMaster={isMaster} uninstall={uninstall} storeName={storeName} onViewProducts={onClose} />
        </DisconnectDialogContent>
    );

    // Shopify/BigCommerce disconnect by uninstalling the app: wider window, no footer button (the seller acts in Shopify).
    if (uninstall) {
        return (
            <Dialog windowDecorator={DisconnectDialogWindow} minHeight={460} onClose={onClose}>
                {content}
            </Dialog>
        );
    }

    // Master and other secondary channels: a destructive Disconnect button in the footer.
    return (
        <Dialog onClose={onClose} buttons={[{ label: T._('Disconnect', 'Admin'), destructive: true, onClick: onClose }]}>
            {content}
        </Dialog>
    );
};
